// Helper file for product filter and sort by logic
import type { IncludedPackageRelations, PbLineItem, SfTag } from '@/apiTypes'
import type { PbLineItemProductAndPricingOptions } from '@/classes/PriceBook'
import { PbSubcategory } from '@/enums/PbSubcategory'

export type SortByOption = {
	name: string
	value: 'alphabetical' | 'price-high-to-low' | 'price-low-to-high'
}

type TagFilterableItem = object & {
	tags?: SfTag[]
}

type SortableProductListItem = Pick<
	PbLineItem,
	'fixedPrice' | 'name' | 'subcategory'
> &
	Partial<IncludedPackageRelations> &
	Partial<Pick<PbLineItemProductAndPricingOptions, 'pricingOptions'>>

/**
 * Whether an item matches the selected tag filter.
 * When `includeUntagged` is true, items without a `tags` property pass
 * (used for packages that do not share product tag shape).
 */
export const matchesSelectedTag = (
	item: TagFilterableItem,
	selectedTag: SfTag | null,
	{ includeUntagged = false }: { includeUntagged?: boolean } = {}
): boolean => {
	if (!selectedTag || selectedTag.id === 'all') {
		return true
	}

	if (!('tags' in item)) {
		return includeUntagged
	}

	return item.tags?.some((tag) => tag.id === selectedTag.id) ?? false
}

/** Prefers "Popular", then preserves relative order of remaining tags. */
export const prioritizePopularTag = (tags: SfTag[]): SfTag[] => {
	return [...tags].sort((a, b) => {
		if (a.name === 'Popular') return -1
		if (b.name === 'Popular') return 1
		return 0
	})
}

/** Build the tags dropdown options with an "All" entry first. */
export const buildProductTagOptions = (
	tags: SfTag[],
	allLabel: string
): SfTag[] => {
	return [{ id: 'all', name: allLabel }, ...prioritizePopularTag(tags)]
}

/**
 * Returns the price represented by a product tile. Products with selectable
 * pricing are sorted by their lowest available option rather than fixedPrice.
 * We want to sort Essential Packages by the sum of their sub items.
 */
export const getProductListSortPrice = (
	item: SortableProductListItem
): number => {
	if (item.pricingOptions?.length) {
		return Math.min(...item.pricingOptions.map((option) => option.price))
	}

	if (
		item.subcategory === PbSubcategory.PackageEssentials &&
		item.packageRelations?.data.length
	) {
		return item.packageRelations.data.reduce(
			(total, relation) => total + (relation.fixedPrice ?? 0),
			0
		)
	}

	return item.fixedPrice
}

/** Return a sorted copy of a product list without mutating its source. */
export const sortProductListItems = <T extends SortableProductListItem>(
	items: T[],
	sortBy: SortByOption['value']
): T[] => {
	return [...items].sort((a, b) => {
		if (sortBy === 'price-high-to-low') {
			return getProductListSortPrice(b) - getProductListSortPrice(a)
		}

		if (sortBy === 'price-low-to-high') {
			return getProductListSortPrice(a) - getProductListSortPrice(b)
		}

		return a.name.localeCompare(b.name)
	})
}
